import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, useCallback, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGameStore } from "./store/gameStore";
import HomePage from "./pages/HomePage";
import FlipTrackingLevel from "./pages/FlipTrackingLevel";
import ColorIdentifyLevel from "./pages/ColorIdentifyLevel";
import FireflyChase from "./pages/FireflyChase";
import MemoryPuzzle from "./pages/MemoryPuzzle";
import ReportPage from "./pages/ReportPage";
import ParentDashboard from "./pages/ParentDashboard";
import ParentEyeReport from "./pages/ParentEyeReport";
import TrainingPlan from "./pages/TrainingPlan";
import AchievementPage from "./pages/AchievementPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ShopPage from "./pages/ShopPage";
import EyeExercise from "./pages/EyeExercise";
import DailyMatchPage from "./pages/DailyMatchPage";
import AchievementNotification from "./components/AchievementNotification";
import DailyCheckin from "./components/DailyCheckin";
import BottomNav from "./components/BottomNav";
import { playNotify } from "./utils/sounds";

// GameEngine 是仓库里最大的单文件（8000+ 行），只有进入具体训练时才需要，
// 首页和导航页都用不到。改为按需加载，把它从首屏主包里挪出去。
const GameEngine = lazy(() => import("./engine/GameEngine"));

function GameEngineLoading() {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center gap-3 bg-[var(--bg)]">
      <div className="w-8 h-8 rounded-full border-2 border-[var(--border)] border-t-[var(--accent)] animate-spin" />
      <p className="text-xs text-[var(--text-muted)]">正在载入训练…</p>
    </div>
  );
}

function EyeRestReminder({ onDismiss }: { onDismiss: () => void }) {
  const [countdown, setCountdown] = useState(20);

  useEffect(() => {
    playNotify();
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.85, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
      >
        <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 flex items-center justify-center">
          <span className="text-4xl">👀</span>
        </div>
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          休息一下吧！
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
          看看远处 6 米以外的地方
        </p>
        <p className="text-gray-400 dark:text-gray-500 text-xs mb-5">
          20-20-20 护眼法则：每 20 分钟远眺 20 秒
        </p>

        {/* Countdown ring */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              className="text-gray-100 dark:text-gray-700"
            />
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              className="text-emerald-500"
              strokeDasharray={`${2 * Math.PI * 42}`}
              strokeDashoffset={`${2 * Math.PI * 42 * (1 - countdown / 20)}`}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {countdown}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          {countdown === 0 ? (
            <button
              onClick={onDismiss}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30 hover:shadow-xl transition-all active:scale-95"
            >
              继续训练 ✨
            </button>
          ) : (
            <button
              onClick={onDismiss}
              className="w-full py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
            >
              跳过
            </button>
          )}
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            💡 也可以做做眼保健操，放松眼部肌肉
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function App() {
  const location = useLocation();
  const inGame = location.pathname.startsWith('/game/');
  const updateConsecutiveDays = useGameStore((s) => s.updateConsecutiveDays);
  const darkMode = useGameStore((s) => s.darkMode);
  const eyeProtection = useGameStore((s) => s.eyeProtection);
  const colorTemperature = useGameStore((s) => s.colorTemperature);
  const eyeReminderEnabled = useGameStore((s) => s.eyeReminderEnabled);
  const eyeReminderInterval = useGameStore((s) => s.eyeReminderInterval);
  const startEyeProtectionTracking = useGameStore((s) => s.startEyeProtectionTracking);
  const stopEyeProtectionTracking = useGameStore((s) => s.stopEyeProtectionTracking);
  const autoNightEyeProtection = useGameStore((s) => s.autoNightEyeProtection);
  const nightStartHour = useGameStore((s) => s.nightStartHour);
  const nightEndHour = useGameStore((s) => s.nightEndHour);
  const toggleEyeProtection = useGameStore((s) => s.toggleEyeProtection);
  const lastCheckinDate = useGameStore((s) => s.lastCheckinDate);
  const [showReminder, setShowReminder] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    updateConsecutiveDays();
  }, [updateConsecutiveDays]);

  // Show daily check-in popup if not checked in today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    if (lastCheckinDate !== today) {
      // Small delay to avoid jarring on load
      const timer = setTimeout(() => setShowCheckin(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [lastCheckinDate]);

  // Auto night eye protection
  useEffect(() => {
    if (!autoNightEyeProtection) return;

    const checkNightMode = () => {
      const hour = new Date().getHours();
      let isNight = false;
      if (nightStartHour > nightEndHour) {
        // Crosses midnight, e.g., 21:00 - 7:00
        isNight = hour >= nightStartHour || hour < nightEndHour;
      } else {
        // Same day range, e.g., 22:00 - 23:00
        isNight = hour >= nightStartHour && hour < nightEndHour;
      }

      const currentState = useGameStore.getState();
      if (isNight && !currentState.eyeProtection) {
        toggleEyeProtection();
      } else if (!isNight && currentState.eyeProtection) {
        toggleEyeProtection();
      }
    };

    checkNightMode();
    const interval = setInterval(checkNightMode, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [autoNightEyeProtection, nightStartHour, nightEndHour, toggleEyeProtection]);

  // Eye protection duration tracking
  useEffect(() => {
    if (eyeProtection) {
      startEyeProtectionTracking();
    } else {
      stopEyeProtectionTracking();
    }
    return () => {
      // On cleanup, stop tracking if active
      if (useGameStore.getState().eyeProtection && useGameStore.getState().eyeProtectionStartTime) {
        useGameStore.getState().stopEyeProtectionTracking();
      }
    };
  }, [eyeProtection]);

  // Persist tracking when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (useGameStore.getState().eyeProtection) {
        useGameStore.getState().stopEyeProtectionTracking();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also stop when component unmounts
      if (useGameStore.getState().eyeProtection) {
        useGameStore.getState().stopEyeProtectionTracking();
      }
    };
  }, []);

  // Sync dark mode class on mount and when darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Eye rest reminder timer
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (eyeReminderEnabled && eyeReminderInterval > 0) {
      timerRef.current = setInterval(() => {
        window.dispatchEvent(new Event('precision-rest'));
        setShowReminder(true);
      }, eyeReminderInterval * 60 * 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [eyeReminderEnabled, eyeReminderInterval]);

  const handleDismissReminder = useCallback(() => {
    setShowReminder(false);
  }, []);

  return (
    <div className={location.pathname === '/' ? 'cyber-shell' : 'min-h-screen w-full overflow-x-hidden relative mx-auto sm:max-w-md md:max-w-lg sm:shadow-2xl sm:border-x sm:border-[var(--border)] bg-[var(--bg)]'}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/game/daily-match" element={<DailyMatchPage />} />
        <Route
          path="/game/:id"
          element={
            <Suspense fallback={<GameEngineLoading />}>
              <GameEngine />
            </Suspense>
          }
        />
        <Route path="/level/flip-tracking" element={<FlipTrackingLevel />} />
        <Route path="/level/color-identify" element={<ColorIdentifyLevel />} />
        <Route path="/level/firefly-chase" element={<FireflyChase />} />
        <Route path="/level/memory-puzzle" element={<MemoryPuzzle />} />
        <Route path="/level/eye-exercise" element={<EyeExercise />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/parent" element={<ParentDashboard />} />
        <Route path="/parent/eye-report" element={<ParentEyeReport />} />
        <Route path="/parent/training-plan" element={<TrainingPlan />} />
        <Route path="/achievements" element={<AchievementPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/shop" element={<ShopPage />} />
      </Routes>
      {/* Eye Protection Overlay */}
      {eyeProtection && (
        <div
          className="fixed inset-0 pointer-events-none z-[9999] transition-opacity duration-500"
          style={{
            background: `rgba(255, 180, 50, ${colorTemperature * 0.002})`,
            mixBlendMode: 'multiply',
          }}
        />
      )}
      {/* Eye Rest Reminder Modal */}
      <AnimatePresence>
        {showReminder && (
          <EyeRestReminder onDismiss={handleDismissReminder} />
        )}
      </AnimatePresence>
      {/* Achievement Unlock Notification */}
      <AchievementNotification />
      {/* Daily Check-in Modal */}
      <AnimatePresence>
        {showCheckin && !inGame && (
          <DailyCheckin onClose={() => setShowCheckin(false)} />
        )}
      </AnimatePresence>
      {/* Global Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default App;
